import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Thumbnail } from '../partials/image/image.types';
import { useTheme } from '../../theming/useTheme';
import { ErrorBoundary } from '../errorHandling/ErrorBoundary';
import { Image } from '../partials/image/Image';
import { AdditionalStyleSheet } from '../components.utils';
import { P, semanticCSS } from '../../typography/semanticTags';
import { BaseUnit } from '../../theming/baseUnitDefinition';
import { createImageWidths } from '../partials/image/image.utils';

export interface SponsorComponentThemeProps {
  dividerColor: string;
  textColor: string;
  baseUnit: BaseUnit;
}

export interface SponsorComponentDataProps {
  text: string;
  image: Thumbnail;
}

const componentConfigurationKey = 'sponsor';

const createSponsorStyle = (baseUnit: BaseUnit, dividerColor: string, textColor: string) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: `${baseUnit}px`,
    marginRight: `${baseUnit}px`,
  },
  text: {
    color: textColor,
    marginLeft: `${baseUnit * 0.5}px`,
  },
  divider: {
    backgroundColor: dividerColor,
    width: 'auto',
    height: '1px',
  },
  topDivider: {
    marginTop: `${baseUnit}px`,
    marginLeft: `${baseUnit}px`,
    marginRight: `${baseUnit}px`,
    marginBottom: `${baseUnit * 0.5}px`,
  },
  bottomDivider: {
    marginLeft: `${baseUnit}px`,
    marginRight: `${baseUnit}px`,
    marginTop: `${baseUnit * 0.5}px`,
  },
  imageWrapper: {
    width: `${baseUnit * 2}px`,
  },
});

const createImageAdditionalStyle = () => StyleSheet.create({
  style: {
    borderRadius: '50%',
  },
}) as AdditionalStyleSheet;

const imageWidths = createImageWidths(300, 300, 300);

export const Sponsor: React.FunctionComponent<SponsorComponentDataProps> = props => {
  const { dividerColor, baseUnit, textColor } = useTheme<SponsorComponentThemeProps>(componentConfigurationKey);
  const { text, image } = props;
  const style = createSponsorStyle(baseUnit, dividerColor, textColor);
  return (
    <ErrorBoundary>
      <div className={css(style.topDivider, style.divider)} />
      <div className={css(style.wrapper)}>
        <div className={css(style.imageWrapper)}>
          <Image {...image} additionalStyle={createImageAdditionalStyle()} imageWidths={imageWidths} />
        </div>
        <P styles={semanticCSS(style.text)}>
          {text}
        </P>
      </div>
      <div className={css(style.bottomDivider, style.divider)} />
    </ErrorBoundary>
  );
};
